import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { GAL } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const { t } = useI18n();
  const [imgs, setImgs] = useState([]);
  const [idx, setIdx] = useState(null);

  useEffect(() => { axios.get(`${API}/gallery`).then((r) => setImgs(r.data.images || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (idx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + imgs.length) % imgs.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, imgs.length]);

  return (
    <div className="pt-28 md:pt-36 pb-20 bg-white">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-3">{t.gallery.eyebrow}</div>
            <h1 className="font-display font-medium text-slate-900 text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">{t.gallery.title}</h1>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-slate-600 text-lg leading-relaxed">{t.gallery.sub}</p>
          </div>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]">
          {imgs.map((src, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.04 }}
              onClick={() => setIdx(i)}
              data-testid={GAL.item}
              className="mb-4 md:mb-5 block w-full break-inside-avoid group relative overflow-hidden rounded-xl bg-slate-100 border border-slate-200 hover:border-slate-300 transition-colors shadow-sm"
            >
              <img src={src} alt="" loading="lazy" className="w-full h-auto transition-transform duration-[1000ms] group-hover:scale-105" />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {idx !== null && (
          <motion.div
            data-testid={GAL.lightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-slate-950/90 backdrop-blur-xl grid place-items-center p-4"
            onClick={() => setIdx(null)}
          >
            <motion.img
              key={idx}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.35 }}
              src={imgs[idx]}
              alt=""
              className="max-w-[92vw] max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button onClick={(e) => { e.stopPropagation(); setIdx(null); }} data-testid={GAL.close}
              className="absolute top-5 right-5 w-11 h-11 grid place-items-center rounded-full bg-white text-slate-900 hover:scale-105 transition-transform shadow-lg" aria-label="Close">
              <X size={18} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + imgs.length) % imgs.length); }}
              className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-md" aria-label="Previous">
              <ChevronLeft size={18} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % imgs.length); }}
              className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-md" aria-label="Next">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 label-eyebrow text-white/70 bg-black/40 px-3 py-1.5 rounded-full">
              {String(idx + 1).padStart(2, "0")} / {String(imgs.length).padStart(2, "0")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
